import type {
  ILolaRecipient,
  IPartyAddress,
  IPartyName,
  ISenderUserData,
  IUserData,
} from "../shared/session/interfaces/session";
import type { ICreateOperationRequest } from "../services/api";

export function formatPartyName(
  name?: IPartyName | IUserData["name"] | null,
): string {
  if (!name?.firstName && !name?.lastName) return "";
  return [name?.firstName, name?.lastName].filter(Boolean).join(" ").trim();
}

export function formatPartyAddress(address?: IPartyAddress | null): string {
  if (!address) return "";
  const cityState = [address.city, address.stateCode].filter(Boolean).join(", ");
  const zip = address.zipCode ?? "";
  return [address.line1, `${cityState} ${zip}`.trim()]
    .filter(Boolean)
    .join(", ")
    .trim();
}

export function senderDisplayName(
  sender?: ISenderUserData,
  userData?: IUserData | null,
): string {
  return (
    formatPartyName(sender?.name) ||
    formatPartyName(userData?.name) ||
    userData?.fullName?.trim() ||
    ""
  );
}

export function recipientFirstName(recipient?: ILolaRecipient): string {
  return recipient?.name?.firstName?.trim() || "";
}

const toCanonicalRecipientName = (
  name: IPartyName,
): NonNullable<ICreateOperationRequest["recipient"]>["name"] => ({
  firstName: name.firstName,
  lastName: name.lastName,
  ...(name.middleName ?? name.secondName
    ? { middleName: name.middleName ?? name.secondName }
    : {}),
  ...(name.secondLastName ? { secondLastName: name.secondLastName } : {}),
});

export function buildRecipientFromConfig(
  recipient: ILolaRecipient | undefined,
): ICreateOperationRequest["recipient"] {
  if (!recipient?.name?.firstName || !recipient?.name?.lastName) {
    return undefined;
  }
  return {
    name: toCanonicalRecipientName(recipient.name),
    ...(recipient.phone ? { phone: recipient.phone } : {}),
    ...(recipient.address
      ? {
          address: {
            ...(recipient.address.country
              ? { countryISO2: recipient.address.country }
              : {}),
            ...(recipient.address.stateCode
              ? { state: recipient.address.stateCode }
              : {}),
            ...(recipient.address.city ? { city: recipient.address.city } : {}),
            ...(recipient.address.line1
              ? { line1: recipient.address.line1 }
              : {}),
            ...(recipient.address.zipCode
              ? { zip: recipient.address.zipCode }
              : {}),
          },
        }
      : {}),
  };
}
